import { Badge } from '@/components/ui/badge';
import {
  AIServiceIcon,
  ChatIcon,
  GPUInstanceIcon,
  LLMServiceIcon,
  OneClickDeployIcon,
  OneClickTrainingIcon,
  PromptIcon,
  QuantizeIcon,
  ServerlessGPUIcon,
} from '@/components/ui/icons';
import siteConfig from '@/config';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';
import useDemoPath from '@/hook/useDemoPath';
import { cn } from '@/lib/utils';
import PATH_PAGE from '@/router/path';
import { CodeXmlIcon, TicketIcon } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';

const LinkService: React.FC = () => {
  const { orgId, workspaceId } = useParamWorkspaceId();
  const { services } = siteConfig;
  const { isDemoMode, withDemoPrefix } = useDemoPath();
  const service = [
    {
      service: services.llm,
      name: 'LLM as a service',
      description: 'Pay-per-token. No upfront cost. No hidden fee.',
      icon: LLMServiceIcon,
      href: withDemoPrefix(PATH_PAGE.dashboard.llm?.root(orgId, workspaceId || '') ?? ''),
      color: 'bg-yellow-500',
    },
    {
      service: services.oneClickDeploy,
      name: 'One click deploy',
      description: 'LLM deployment using HuggingFace repo.',
      icon: OneClickDeployIcon,
      href: withDemoPrefix(PATH_PAGE.dashboard.oneClickDeploy?.root(orgId, workspaceId || '') ?? ''),
      color: 'bg-red-500',
    },
    {
      service: services.serverlessGPU,
      name: 'Serverless GPU',
      description:
        'Instantly access powerful GPUs, pay only for usage, and run AI workloads without managing infrastructure.',
      icon: ServerlessGPUIcon,
      color: 'bg-cyan-500',
      href: withDemoPrefix(PATH_PAGE.dashboard.serverlessGPU?.root(orgId, workspaceId) ?? ''),
    },
    {
      service: services.gpuInstance,
      name: 'GPU Instance',
      description:
        'Create dedicated GPU instances with SSH access for development and training.',
      icon: GPUInstanceIcon,
      color: 'bg-indigo-500',
      href: withDemoPrefix(PATH_PAGE.dashboard.gpuInstance?.root(orgId, workspaceId) ?? ''),
      isNew: true,
    },
    {
      service: services.ai,
      name: 'AI as a service',
      description:
        'Ready-to-use AI services via API, integrate instantly into your project.',
      icon: AIServiceIcon,
      color: 'bg-fuchsia-500',
      href: withDemoPrefix(PATH_PAGE.dashboard.ai?.root(orgId, workspaceId) ?? ''),
      isNew: true,
    },
    {
      service: services.oneClickTraining,
      name: 'One click training',
      description: 'Coming soon.',
      icon: OneClickTrainingIcon,
      newTab: true,
      color: 'bg-slate-400',
    },
  ];
  const resource = [
    {
      //colab https://colab.float16.cloud/
      name: 'Colab',
      description:
        'Colab is a online tool that allows you to write and execute Python code through the browser.',
      icon: CodeXmlIcon,
      href: 'https://colab.float16.cloud/',
      newTab: true,
      color: 'bg-orange-500',
      isNew: true,
    },
    {
      name: 'Quantize',
      description:
        'Compare LLM inference speeds with different quantization techniques and KV cache settings.',
      icon: QuantizeIcon,
      href: 'https://quantize.float16.cloud/',
      newTab: true,
      color: 'bg-violet-500',
    },
    {
      name: 'Chatbot',
      description:
        'Chat interface to interact with LLMaaS and custom LLM model.',
      icon: ChatIcon,
      href: 'https://chat.float16.cloud/',
      newTab: true,
      color: 'bg-emerald-500',
    },
    {
      name: 'Prompt',
      description: 'Create Prompt, Run and Share with your colleague.',
      icon: PromptIcon,
      href: 'https://prompt.float16.cloud/',
      newTab: true,
      color: 'bg-rose-500',
    },
    {
      name: 'Apply Coupon',
      description: 'Redeem your coupon code to get credits.',
      icon: TicketIcon,
      href: PATH_PAGE.setting.coupon,
      color: 'bg-amber-500',
    },
  ];
  return (
    <div>
      <p className="text-xl font-medium">Service</p>
      <div className="mt-4 grid grid-cols-1 gap-4 gap-y-8 md:grid-cols-2 lg:grid-cols-3">
        {service
          .filter((item) => item.service)
          .filter((item) => !(isDemoMode && item.name === 'AI as a service'))
          .map((item, index) => {
            return <LinkItem key={index} {...item} />;
          })}
      </div>
      {!isDemoMode && (
        <>
          <p className="mt-8 text-xl font-medium">Resource</p>
          <div className="mt-4 grid grid-cols-1 gap-4 gap-y-8 md:grid-cols-2 lg:grid-cols-3">
            {resource.map((item, index) => {
              return <LinkItem key={index} {...item} />;
            })}
          </div>
        </>
      )}
    </div>
  );
};
export default LinkService;

interface LinkItemProps {
  href?: string;
  newTab?: boolean;
  name: string;
  description: string;
  icon: any;
  color: string;
  isNew?: boolean;
}

const LinkItem: React.FC<LinkItemProps> = ({
  href,
  newTab,
  name,
  description,
  icon,
  color,
  isNew,
}) => {
  const ComponentIcon = icon;
  return (
    <Link
      to={href || ''}
      target={newTab ? '_blank' : '_self'}
      className={cn('group block', !href && 'pointer-events-none')}
    >
      <div className="flex items-start gap-4">
        <div
          className={cn(
            'flex max-h-12 min-h-12 min-w-12 max-w-12 items-center justify-center overflow-hidden rounded-lg text-white',
            color,
            !href && 'bg-gray-300'
          )}
        >
          <ComponentIcon className="transition-al l h-6 w-5 duration-300 group-hover:h-6 group-hover:w-6" />
        </div>
        <div>
          <div className="flex gap-2">
            <p className="text-sm font-medium">{name}</p>
            {isNew && <Badge variant={'success'}>new</Badge>}
          </div>

          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
    </Link>
  );
};
